import { useState, useEffect } from 'react'
import { ChevronRight, ChevronLeft, X } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
// Import background images
import ServicesBG from '@/assets/images/backgrounds/ServicesBG.jpg'

// Import service icons
import HardscapesIcon from '@/assets/icons/Hardscapes.png'
import ArchitecturalConcreteIcon from '@/assets/icons/Architectural & Specialty Concrete Formwork.png'
import ConsultingIcon from '@/assets/icons/Consulting & Project Planning.png'
import PrecastIcon from '@/assets/icons/Precast Concrete.png'
import PanelizedIcon from '@/assets/icons/Panelized Building Envelope Installation.png'
import MassTimberIcon from '@/assets/icons/Masstimber Timber Installation.png'
import SteelFabricationIcon from '@/assets/icons/SpecializedSteelDesignAndFabrication.png'

// Import service images
import Precast1 from '@/assets/images/services/precast/Precast1.jpg'
import Precast2 from '@/assets/images/services/precast/Precast2.jpeg'
import Precast3 from '@/assets/images/services/precast/Precast3.jpeg'
import Precast4 from '@/assets/images/services/precast/Precast4.jpeg'
import Precast5 from '@/assets/images/services/precast/Precast5.jpeg'
import Precast6 from '@/assets/images/services/precast/Precast6.jpeg'
import Precast7 from '@/assets/images/services/precast/Precast7.jpeg'
import Precast8 from '@/assets/images/services/precast/Precast8.jpeg'
import Precast9 from '@/assets/images/services/precast/Precast9.jpeg'
import Precast10 from '@/assets/images/services/precast/Precast10.jpeg'
import Precast11 from '@/assets/images/services/precast/Precast11.jpeg'
import Architectural1 from '@/assets/images/services/architectural/Architectural1.jpg'
import Architectural2 from '@/assets/images/services/architectural/Architectural2.jpg'
import Architectural3 from '@/assets/images/services/architectural/Architectural3.jpeg'
import Architectural4 from '@/assets/images/services/architectural/Architectural4.jpeg'
import Architectural5 from '@/assets/images/services/architectural/Architectural5.jpeg'
import Architectural6 from '@/assets/images/services/architectural/Architectural6.jpeg'
import Architectural7 from '@/assets/images/services/architectural/Architectural7.jpeg'
import Consulting1 from '@/assets/images/services/consulting/Consulting1.jpeg'
import Consulting2 from '@/assets/images/services/consulting/Consulting2.jpeg'
import Consulting3 from '@/assets/images/services/consulting/Consulting3.jpeg'
import Consulting4 from '@/assets/images/services/consulting/Consulting4.jpeg'
import Panelized1 from '@/assets/images/services/panelized/Panelized1.jpg'
import Panelized2 from '@/assets/images/services/panelized/Panelized2.JPG'
import Panelized3 from '@/assets/images/services/panelized/Panelized3.jpeg'
import Panelized4 from '@/assets/images/services/panelized/Panelized4.jpeg'
import Panelized5 from '@/assets/images/services/panelized/Panelized5.jpeg'
import Panelized6 from '@/assets/images/services/panelized/Panelized6.jpeg'
import Panelized7 from '@/assets/images/services/panelized/Panelized7.jpeg'
import Panelized8 from '@/assets/images/services/panelized/Panelized8.jpeg'
import Panelized9 from '@/assets/images/services/panelized/Panelized9.jpeg'
import Masstimber1 from '@/assets/images/services/mass-timber/Masstimber1.jpg'
import Masstimber2 from '@/assets/images/services/mass-timber/Masstimber2.JPG'
import Masstimber3 from '@/assets/images/services/mass-timber/Masstimber3.JPG'
import Masstimber4 from '@/assets/images/services/mass-timber/Masstimber4.JPG'
import Masstimber5 from '@/assets/images/services/mass-timber/Masstimber5.jpeg'
import Masstimber6 from '@/assets/images/services/mass-timber/Masstimber6.jpeg'
import Hardscaped1 from '@/assets/images/services/hardscapes/Hardscaped1.jpg'
import Hardscaped2 from '@/assets/images/services/hardscapes/Hardscaped2.jpg'
import Hardscaped3 from '@/assets/images/services/hardscapes/Hardscaped3.jpg'
import Hardscaped4 from '@/assets/images/services/hardscapes/Hardscaped4.jpg'
import Hardscaped5 from '@/assets/images/services/hardscapes/Hardscaped5.png'
import Hardscaped6 from '@/assets/images/services/hardscapes/Hardscaped6.png'
import SteelFabrication1 from '@/assets/images/services/steel-fabrication/SteelFabrication1.jpg'
import SteelFabrication2 from '@/assets/images/services/steel-fabrication/SteelFabrication2.jpeg'
import SteelFabrication3 from '@/assets/images/services/steel-fabrication/SteelFabrication3.jpeg'
import SteelFabrication4 from '@/assets/images/services/steel-fabrication/SteelFabrication4.jpeg'
import SteelFabrication5 from '@/assets/images/services/steel-fabrication/SteelFabrication5.jpeg'
import SteelFabrication6 from '@/assets/images/services/steel-fabrication/SteelFabrication6.jpeg'
import SteelFabrication7 from '@/assets/images/services/steel-fabrication/SteelFabrication7.jpeg'
import SteelFabrication8 from '@/assets/images/services/steel-fabrication/SteelFabrication8.jpeg'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import ScrollIndicator from '@/components/ScrollIndicator'

interface Service {
  title: string
  shortDescription: string
  fullDescription: string
  images: string[]
  features: string[]
  category: 'Residential' | 'Commercial'
  subcategory: string
  icon: string // Imported icon image
  gradient: string // Tailwind gradient classes
}

export default function Services() {
  const [isVisible, setIsVisible] = useState(false)
  const fullText = 'Our Services'
  const sloganFullText = 'Comprehensive construction solutions delivered with excellence and innovation.'

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  const services: Service[] = [
    // RESIDENTIAL SERVICES
    {
      title: "Hardscapes",
      shortDescription: "Beautiful hardscape features that enhance outdoor living spaces.",
      fullDescription: "Hardscaping is the backbone of any outdoor space, incorporating non-living elements like stone, concrete, wood, and man-made structures to create functional and visually stunning environments. At TAKT Construction, we excel in designing and building hardscape features that blend seamlessly with your landscape.",
      images: [Hardscaped1, Hardscaped2, Hardscaped3, Hardscaped4, Hardscaped5, Hardscaped6],
      features: ["Retaining Walls", "Stone Pathways", "Outdoor Kitchens", "Fire Pits"],
      category: "Residential",
      subcategory: "Landscape Design",
      icon: HardscapesIcon,
      gradient: "from-green-500 to-emerald-600"
    },
    {
      title: "Architectural & Specialty Concrete Formwork",
      shortDescription: "Custom concrete finishes and architectural elements for homes.",
      fullDescription: "Our architectural concrete services bring innovative design and precision engineering to your home projects. From decorative concrete finishes and custom formwork to structural elements and aesthetic features, we create beautiful, durable concrete solutions that enhance your residential property's value and appeal.",
      images: [Architectural1, Architectural2, Architectural3, Architectural4, Architectural5, Architectural6, Architectural7],
      features: ["Custom Finishes", "Decorative Elements", "Aesthetic Appeal", "Residential Focus"],
      category: "Residential",
      subcategory: "Specialty Construction",
      icon: ArchitecturalConcreteIcon,
      gradient: "from-gray-500 to-slate-600"
    },
    {
      title: "Consulting & Project Planning",
      shortDescription: "Expert guidance for residential construction and renovation projects.",
      fullDescription: "Our consulting services provide homeowners with expert guidance throughout their construction or renovation journey. From initial planning and design consultation to project management and quality assurance, we ensure your residential project meets your vision, budget, and timeline expectations.",
      images: [Consulting1, Consulting2, Consulting3, Consulting4],
      features: ["Project Planning", "Design Consultation", "Budget Management", "Quality Assurance"],
      category: "Residential",
      subcategory: "Professional Services",
      icon: ConsultingIcon,
      gradient: "from-purple-500 to-indigo-600"
    },
    
    // COMMERCIAL SERVICES
    {
      title: "Precast Concrete Services",
      shortDescription: "Specialized precast concrete solutions for large-scale projects.",
      fullDescription: "At TAKT Construction, we specialize in precast concrete solutions for a wide range of projects. Precast concrete is poured and cured in a controlled environment, ensuring superior quality, durability, and faster installation. Once transported to the site, precast elements can be installed immediately, reducing construction timelines and costs.",
      images: [Precast1, Precast2, Precast3, Precast4, Precast5, Precast6, Precast7, Precast8, Precast9, Precast10, Precast11],
      features: ["Superior Quality", "Faster Installation", "Cost Effective", "Sustainability"],
      category: "Commercial",
      subcategory: "Concrete Solutions",
      icon: PrecastIcon,
      gradient: "from-orange-500 to-red-600"
    },
    {
      title: "Panelized Building Envelope Installation",
      shortDescription: "Modern panelized building envelope systems for energy efficiency.",
      fullDescription: "Our panelized building envelope systems offer a modern solution to construction challenges by combining precision engineering with sustainable practices. Prefabricated in a controlled factory environment, these panels ensure superior energy efficiency by minimizing thermal bridging and maximizing insulation performance.",
      images: [Panelized1, Panelized2, Panelized3, Panelized4, Panelized5, Panelized6, Panelized7, Panelized8, Panelized9],
      features: ["Energy Efficient", "Sustainable", "Durable", "Fast Installation"],
      category: "Commercial",
      subcategory: "Modern Building Systems",
      icon: PanelizedIcon,
      gradient: "from-teal-500 to-cyan-600"
    },
    {
      title: "Mass Timber Installation",
      shortDescription: "Leading mass timber solutions for sustainable construction.",
      fullDescription: "Mass timber is revolutionizing construction in British Columbia and globally, and TAKT Construction is leading the way with innovative, high-quality solutions. By leveraging mass timber's exceptional fire resistance, strength-to-weight ratio, and sustainability, TAKT delivers cutting-edge projects that meet modern building codes.",
      images: [Masstimber1, Masstimber2, Masstimber3, Masstimber4, Masstimber5, Masstimber6],
      features: ["Fire Resistant", "Sustainable", "Seismic Resilient", "Carbon Sequestration"],
      category: "Commercial",
      subcategory: "Sustainable Construction",
      icon: MassTimberIcon,
      gradient: "from-amber-500 to-orange-600"
    },
    {
      title: "Architectural & Specialty Concrete Formwork",
      shortDescription: "Large-scale architectural concrete solutions for commercial projects.",
      fullDescription: "Our architectural concrete services deliver innovative, large-scale concrete solutions for commercial buildings, industrial facilities, and infrastructure projects. We combine precision engineering with cutting-edge techniques to create custom concrete finishes, structural elements, and architectural features that meet commercial building codes and performance standards.",
      images: [Architectural1, Architectural2, Architectural3, Architectural4, Architectural5, Architectural6, Architectural7],
      features: ["Large-Scale Projects", "Commercial Focus", "Building Code Compliance", "Structural Integrity"],
      category: "Commercial",
      subcategory: "Specialty Construction",
      icon: ArchitecturalConcreteIcon,
      gradient: "from-indigo-500 to-purple-600"
    },
    {
      title: "Consulting & Project Planning",
      shortDescription: "Comprehensive project management for commercial construction projects.",
      fullDescription: "Our consulting and project management services address the complex challenges of large-scale construction projects. From preconstruction planning and feasibility analysis to contract administration and project oversight, we provide the expertise needed to deliver commercial projects on time, on budget, and to the highest quality standards.",
      images: [Consulting1, Consulting2, Consulting3, Consulting4],
      features: ["Project Management", "Contract Administration", "Risk Management", "Quality Control"],
      category: "Commercial",
      subcategory: "Professional Services",
      icon: ConsultingIcon,
      gradient: "from-pink-500 to-rose-600"
    },
    {
      title: "Specialized steel design & fabrication",
      shortDescription: "Specialized steel design and fabrication.",
      fullDescription: "At TAKT Construction, we provide comprehensive steel fabrication services for commercial and industrial projects. Our experienced team specializes in custom steel design and fabrication, delivering high-quality structural steel components, frames, and custom metalwork. We work with precision engineering to ensure every steel element meets exact specifications and building code requirements, providing durable and reliable solutions for your construction needs.",
      images: [SteelFabrication1, SteelFabrication2, SteelFabrication3, SteelFabrication4, SteelFabrication5, SteelFabrication6, SteelFabrication7, SteelFabrication8],
      features: ["Custom Design", "Precision Engineering", "Code Compliance", "Quality Craftsmanship"],
      category: "Commercial",
      subcategory: "Metal Fabrication",
      icon: SteelFabricationIcon,
      gradient: "from-slate-500 to-gray-600"
    }
  ]

  const [searchParams, setSearchParams] = useSearchParams()
  const categoryParam = searchParams.get('category')
  const initialCategory = categoryParam === 'residential' ? 'Residential' : categoryParam === 'commercial' ? 'Commercial' : 'Commercial'
  
  const [selectedCategory, setSelectedCategory] = useState<'Residential' | 'Commercial'>(initialCategory)
  
  // Handler to update category and URL
  const handleCategoryChange = (category: 'Residential' | 'Commercial') => {
    setSelectedCategory(category)
    setSearchParams({ category: category.toLowerCase() })
  }

  // Modal state
  const [modalServiceIndex, setModalServiceIndex] = useState<number | null>(null)
  const [modalImageIndex, setModalImageIndex] = useState(0)

  // Filter services based on selected category
  const filteredServices = services.filter(service => service.category === selectedCategory)
  
  // Update category when URL parameter changes
  useEffect(() => {
    const categoryParam = searchParams.get('category')
    if (categoryParam === 'residential') {
      setSelectedCategory('Residential')
    } else if (categoryParam === 'commercial') {
      setSelectedCategory('Commercial')
    } else {
      // Default to Commercial if no category param
      setSelectedCategory('Commercial')
    }
  }, [searchParams])

  // Intersection Observer for animations
  useEffect(() => {
    setIsVisible(false) // Reset visibility when category changes
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1 }
    )

    const element = document.getElementById('services')
    if (element) {
      observer.observe(element)
    }

    return () => observer.disconnect()
  }, [selectedCategory]) // Re-observe when category changes

  // Dropdown removed; details live only in modal

  // Modal helpers
  const openModal = (index: number) => {
    setModalServiceIndex(index)
    setModalImageIndex(0)
  }

  const closeModal = () => {
    setModalServiceIndex(null)
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    if (modalServiceIndex !== null) {
      window.addEventListener('keydown', onKeyDown)
    }
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [modalServiceIndex])

  return (
    <>
      <Header currentPage="services" />

      {/* Fixed Background Image - Stays fixed while scrolling */}
      <div className="fixed inset-0 w-full h-full z-0">
        <img
          src={ServicesBG}
          alt="TAKT Construction Background"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay for hero section */}
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden z-10">
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h1
              className={`text-5xl md:text-7xl font-bold text-white mb-8 leading-tight transition-all duration-1000 ease-out ${
                isVisible
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-75'
              }`}
            >
              {fullText}
            </h1>
            <p
              className={`text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12 transition-all duration-1000 ease-out delay-300 ${
                isVisible
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-75'
              }`}
            >
              {sloganFullText}
            </p>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <ScrollIndicator targetId="services" />
      </section>


      {/* Services - Card Grid Layout */}
      <section id="services" className="py-20 lg:py-32 relative overflow-hidden z-10">
        {/* Transparent overlay to show fixed background through */}
        <div className="absolute inset-0 z-0 bg-white/60 backdrop-blur-sm"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          {/* Section header */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Service Category Tabs */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-lg bg-slate-100/90 backdrop-blur-sm p-1">
                <button
                  onClick={() => handleCategoryChange('Residential')}
                  className={`px-8 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                    selectedCategory === 'Residential'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Residential
                </button>
                <button
                  onClick={() => handleCategoryChange('Commercial')}
                  className={`px-8 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                    selectedCategory === 'Commercial'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Commercial
                </button>
              </div>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              {selectedCategory} Services
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Discover the comprehensive construction solutions that make {selectedCategory.toLowerCase()} services the preferred choice for clients throughout the Greater Vancouver Region.
            </p>
          </div>

          {/* Services grid - using flexbox to center odd items */}
          <div className="flex flex-wrap justify-center gap-8">
            {filteredServices.map((service, index) => (
              <div
                key={index}
                className={`group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl border border-gray-100 hover:border-gray-200 transition-all duration-500 transform hover:-translate-y-3 hover:scale-105 cursor-pointer ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                } w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.333rem)] flex-shrink-0`}
                style={{ 
                  transitionDelay: `${index * 150}ms`,
                  maxWidth: '384px' // max-w-sm equivalent
                }}
                onClick={() => openModal(index)}
              >
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-500`}></div>

                {/* Icon */}
                <div className="relative mb-6 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-lg shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 overflow-hidden bg-slate-100 flex items-center justify-center relative">
                    {/* Fallback - always rendered */}
                    <div className="absolute inset-0 w-16 h-16 bg-slate-300 rounded-lg flex items-center justify-center m-auto">
                      <span className="text-2xl text-slate-600 font-bold">
                        {service.title.charAt(0)}
                      </span>
                    </div>
                    {/* Icon image - overlays fallback if it loads successfully */}
                    {service.icon && (
                      <img
                        src={service.icon}
                        alt={service.title}
                        className="w-full h-full object-cover relative z-10"
                        onError={(e) => {
                          // Hide image if it fails to load, fallback will show through
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-slate-900 mb-4 group-hover:text-slate-700 transition-colors duration-300 text-center">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-300">
                  {service.shortDescription}
                </p>

                {/* Hover effect line */}
                <div
                  className="absolute bottom-0 left-8 right-8 h-1 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 bg-blue-600"
                ></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {modalServiceIndex !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-0 md:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal()
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full h-full md:h-auto md:max-w-[95vw] md:max-h-[95vh] rounded-none md:rounded-xl bg-white shadow-2xl overflow-y-auto">
            {/* Close - Mobile optimized */}
            <button
              onClick={closeModal}
              className="fixed md:absolute right-4 top-4 z-20 inline-flex items-center justify-center rounded-full bg-white/90 md:bg-white/90 p-3 md:p-2 text-slate-600 hover:bg-white hover:text-slate-800 shadow-lg md:shadow-md transition-all duration-200"
              aria-label="Close"
            >
              <X className="h-6 w-6 md:h-5 md:w-5" />
            </button>

            {/* Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* Carousel */}
              <div className="relative h-[400px] sm:h-[500px] md:h-[800px] lg:h-[900px] overflow-hidden bg-slate-50 flex items-center justify-center p-4 md:p-6">
                <div className="w-full h-full rounded-lg overflow-hidden shadow-lg">
                  <img
                    key={modalImageIndex}
                    src={filteredServices[modalServiceIndex]?.images[modalImageIndex]}
                    alt={filteredServices[modalServiceIndex]?.title}
                    className="h-full w-full object-cover"
                    style={{
                      imageRendering: '-webkit-optimize-contrast',
                      backfaceVisibility: 'hidden',
                      transform: 'translateZ(0)',
                      WebkitTransform: 'translateZ(0)',
                      willChange: 'transform',
                      animation: 'fadeIn 0.6s ease-in-out',
                    }}
                    loading="eager"
                  />
                </div>
                {filteredServices[modalServiceIndex]?.images.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setModalImageIndex((i) =>
                          (i - 1 + filteredServices[modalServiceIndex].images.length) %
                          filteredServices[modalServiceIndex].images.length
                        )
                      }
                      className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-800 shadow hover:bg-white"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() =>
                        setModalImageIndex((i) =>
                          (i + 1) % filteredServices[modalServiceIndex].images.length
                        )
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-800 shadow hover:bg-white"
                      aria-label="Next image"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                      {filteredServices[modalServiceIndex].images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setModalImageIndex(idx)}
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            modalImageIndex === idx ? 'w-6 bg-white' : 'w-3 bg-white/60 hover:bg-white/80'
                          }`}
                          aria-label={`Go to image ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Details */}
              <div className="p-6 md:p-10 lg:p-12 flex flex-col justify-start bg-slate-50 md:h-[800px] lg:h-[900px] overflow-y-auto">
                <div className="space-y-6">
                  <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white">
                    {filteredServices[modalServiceIndex]?.subcategory}
                  </span>
                  <h3 className="text-2xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight text-center">
                    {filteredServices[modalServiceIndex]?.title}
                  </h3>
                  <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
                    {filteredServices[modalServiceIndex]?.shortDescription}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {filteredServices[modalServiceIndex]?.features.map((feature, fIdx) => (
                      <span
                        key={fIdx}
                        className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-4 py-2 text-sm md:text-base font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                  <p className="text-slate-700 leading-relaxed text-base md:text-lg">
                    {filteredServices[modalServiceIndex]?.fullDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  )
} 


